import { Injectable } from "@angular/core";
import { EMPTY, Observable, catchError, first, tap } from "rxjs";

import { LoggerService } from "../logger/logger.service";
import { HttpClientHelperService } from "../api/http-client-helper.service";
import { DatePipe } from "@angular/common";
import { MineSnackbarService } from "../shared/mine-snackbar/mine-snackbar.service";
import { MineSnackbarType } from "../shared/mine-snackbar/mine-snackbar-type";
import { ContentPipe } from "../services/content/content.pipe";
import { PdfConversionRequest } from "./pdf-generator.interface";

@Injectable({
	providedIn: 'root'
})
export class PdfGeneratorService {
    private readonly loggerName = 'PdfGeneratorService';

    constructor(
        private loggerService: LoggerService,
        private snackbarService: MineSnackbarService,
        private httpClientHelper: HttpClientHelperService,
        private datePipe: DatePipe,
        private contentPipe: ContentPipe
    ) {}

    sendRequest(requestData: PdfConversionRequest): Observable<ArrayBuffer> {
        this.loggerService.info(this.loggerName, 'Starting to create assessments PDF');
        const payload = {
            ...requestData,
            data: JSON.stringify(requestData.data),
        };
        return this.httpClientHelper.invokePostPdfAuth<ArrayBuffer>('reports/v1/pdfexport', payload).pipe(
            first(),
            tap(() => this.loggerService.info(this.loggerName, 'PDF downloaded successfully')),
            tap(() =>  this.snackbarService.showTimed(MineSnackbarType.Confirmation, this.contentPipe.transform('pdf-generator.exportFinished'))),
            catchError(error => {
                this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('pdf-generator.exportError'));
                this.loggerService.error(this.loggerName, this.contentPipe.transform('pdf-generator.exportError'));
                console.error(error);
                return EMPTY;
            }),
        );
    }

    public triggerDownload(buffer: ArrayBuffer, desiredFileName?: string): void {
        const link = document.createElement('a');
        const blob = new Blob([buffer], { type: 'application/pdf' });
        const fileName = desiredFileName ?? `Assessments Report - ${this.datePipe.transform(new Date(), 'dd MMM yyyy')}`;

        link.href = window.URL.createObjectURL(blob);
        link.download = fileName + '.pdf';
        link.click();
    }

    public triggerDocxDownload(buffer: ArrayBuffer, desiredFileName?: string): void {
        const link = document.createElement('a');
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const fileName = desiredFileName ?? `Assessments Report - ${this.datePipe.transform(new Date(), 'dd MMM yyyy')}`;

        link.href = window.URL.createObjectURL(blob);
        link.download = fileName + '.docx';
        link.click();
    }

    sendCopyReportRequest(requestData: PdfConversionRequest): Observable<ArrayBuffer> {
        this.loggerService.info(this.loggerName, 'Starting to create Copy Report PDF');
        const payload = {
            ...requestData,
            data: JSON.stringify(requestData.data),
        };
        return this.httpClientHelper.invokePostPdfAuth<ArrayBuffer>('reports/v1/export-copy-report', payload).pipe(
            first(),
            tap(() => this.loggerService.info(this.loggerName, 'PDF downloaded successfully')),
            tap(() =>  this.snackbarService.showTimed(MineSnackbarType.Confirmation, this.contentPipe.transform('pdf-generator.exportFinished'))),
            catchError(error => {
                this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('pdf-generator.exportError'));
                this.loggerService.error(this.loggerName, this.contentPipe.transform('pdf-generator.exportError'));
                console.error(error);
                return EMPTY;
            }),
        );
    }
}